import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { Home, ArrowLeft, Save, ShieldCheck, CreditCard, Building2, Phone, Mail, User } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import AppButton from '@/components/ui/AppButton';
import ThemeToggle from '@/components/ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PerfilAdmin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    nombre_titular: '',
    banco: '',
    tipo_cuenta: 'Corriente',
    numero_cuenta: '',
    cedula_ruc: '',
    correo_pagos: '',
    numero_whatsapp: ''
  });

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchConfiguracion();
  }, [user]);

  const fetchConfiguracion = async () => {
    try {
      const response = await axios.get(`${API}/admin/configuracion`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConfig(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await axios.put(`${API}/admin/configuracion`, config);
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center gradient-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-background">
      {/* Header Premium */}
      <div className="bg-card/50 dark:bg-card/40 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-primary/10 flex items-center justify-center border border-primary/20 shrink-0">
                <ShieldCheck className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <h1 className="text-sm sm:text-xl md:text-2xl font-black tracking-tight text-foreground uppercase truncate">Configuración</h1>
            </div>
            
            <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
              <ThemeToggle />
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/admin')} 
                icon={ArrowLeft}
                className="h-9 px-2 sm:px-4"
                data-testid="back-btn"
              >
                <span className="hidden sm:inline">Volver</span>
              </AppButton>
              <AppButton 
                variant="secondary" 
                size="sm"
                onClick={() => navigate('/')} 
                icon={Home}
                className="h-9 px-2 sm:px-4"
                data-testid="home-btn"
              >
                <span className="hidden sm:inline">Inicio</span>
              </AppButton>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <Card className="overflow-hidden border-none shadow-2xl bg-card/50 dark:bg-white/5 backdrop-blur-2xl ring-1 ring-white/10 relative">
          {/* Decorative Glow */}
          <div className="absolute -top-24 -left-24 w-64 h-64 bg-primary/5 blur-[100px] rounded-full pointer-events-none"></div>
          
          <CardHeader className="border-b border-white/5 pb-8 relative z-10">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-2xl bg-primary/10 border border-primary/20 shadow-inner">
                <CreditCard className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-xl font-black uppercase tracking-tight text-foreground">Datos de Pago y Contacto</CardTitle>
            </div>
          </CardHeader>
          
          <CardContent className="pt-8 relative z-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Holder Name */}
                <div className="space-y-2">
                  <Label htmlFor="nombre_titular" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Nombre del Titular</Label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="nombre_titular"
                      name="nombre_titular"
                      value={config.nombre_titular}
                      onChange={handleInputChange}
                      required
                      className="h-12 pl-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                      data-testid="nombre-titular-input"
                    />
                  </div>
                </div>

                {/* Bank */}
                <div className="space-y-2">
                  <Label htmlFor="banco" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Banco</Label>
                  <div className="relative group">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="banco"
                      name="banco"
                      value={config.banco}
                      onChange={handleInputChange}
                      required
                      className="h-12 pl-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                      data-testid="banco-input"
                    />
                  </div>
                </div>

                {/* Account Type */}
                <div className="space-y-2">
                  <Label htmlFor="tipo_cuenta" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Tipo de Cuenta</Label>
                  <Select value={config.tipo_cuenta} onValueChange={(value) => setConfig(prev => ({ ...prev, tipo_cuenta: value }))}>
                    <SelectTrigger className="h-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-white/10 backdrop-blur-3xl">
                      <SelectItem value="Corriente">Corriente</SelectItem>
                      <SelectItem value="Ahorros">Ahorros</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Account Number */}
                <div className="space-y-2">
                  <Label htmlFor="numero_cuenta" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Número de Cuenta</Label>
                  <Input
                    id="numero_cuenta"
                    name="numero_cuenta"
                    value={config.numero_cuenta}
                    onChange={handleInputChange}
                    required
                    className="h-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                    data-testid="numero-cuenta-input"
                  />
                </div>

                {/* ID/RUC */}
                <div className="space-y-2">
                  <Label htmlFor="cedula_ruc" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Cédula del Titular / RUC</Label>
                  <Input
                    id="cedula_ruc"
                    name="cedula_ruc"
                    value={config.cedula_ruc}
                    onChange={handleInputChange}
                    required
                    className="h-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                    data-testid="cedula-ruc-input"
                  />
                </div>

                {/* Payment Email */}
                <div className="space-y-2">
                  <Label htmlFor="correo_pagos" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Correo para comprobantes</Label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="correo_pagos"
                      name="correo_pagos"
                      type="email"
                      value={config.correo_pagos}
                      onChange={handleInputChange}
                      required
                      className="h-12 pl-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                      data-testid="correo-pagos-input"
                    />
                  </div>
                </div>

                {/* WhatsApp */}
                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="numero_whatsapp" className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">Número de WhatsApp corporativo</Label>
                  <div className="relative group">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                    <Input
                      id="numero_whatsapp"
                      name="numero_whatsapp"
                      value={config.numero_whatsapp}
                      onChange={handleInputChange}
                      placeholder="+593987654321"
                      required
                      className="h-12 pl-12 rounded-2xl bg-black/[0.03] dark:bg-white/5 border-black/5 dark:border-white/10 focus:ring-primary/20 transition-all font-medium"
                      data-testid="whatsapp-input"
                    />
                  </div>
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest ml-1">
                    Este número se mostrará a los usuarios para soporte y dudas.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t border-white/5">
                <AppButton 
                  type="button" 
                  variant="ghost" 
                  onClick={() => navigate('/admin')}
                >
                  Cancelar
                </AppButton>
                <AppButton 
                  type="submit" 
                  variant="primary"
                  loading={saving}
                  icon={Save}
                  className="px-10 shadow-xl shadow-primary/20"
                  data-testid="guardar-config-btn"
                >
                  Guardar Configuración
                </AppButton>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PerfilAdmin;
